import * as React from 'react';

import type { IMessage } from 'widget/types/messages';

type UseUnreadMessagesResult = {
  unreadCount: number;
  resetUnread: () => void;
};

/**
 * Отслеживает количество непрочитанных сообщений от бота,
 * когда пользователь проскроллен вверх от нижней части чата.
 */
const useUnreadMessages = (
  messages: IMessage[],
  isNearBottom: boolean
): UseUnreadMessagesResult => {
  const [unreadCount, setUnreadCount] = React.useState(0);
  const lastMessageIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const lastMessageId = lastMessage?.id ?? null;

    // Первая загрузка — просто запоминаем ID
    if (lastMessageIdRef.current === null) {
      lastMessageIdRef.current = lastMessageId;
      return;
    }

    // Новое сообщение от бота, когда пользователь не внизу
    if (lastMessageId && lastMessageId !== lastMessageIdRef.current) {
      if (lastMessage?.isBot && !isNearBottom) {
        setUnreadCount((prev) => prev + 1);
      }
      lastMessageIdRef.current = lastMessageId;
    }
  }, [messages, isNearBottom]);

  React.useEffect(() => {
    if (isNearBottom && unreadCount > 0) {
      setUnreadCount(0);
    }
  }, [isNearBottom, unreadCount]);

  const resetUnread = React.useCallback((): void => {
    setUnreadCount(0);
  }, []);

  return { unreadCount, resetUnread };
};

export default useUnreadMessages;
